import * as React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Button,
  Alert,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { deleteCustomerData } from "../../Services/customerAccessService";
import DeletionSummary from "./DeletionSummary";

const KEPT_ITEMS = [
  "Orders - every order, in full",
  "The Shopify buyer record (customers)",
  "Billing / shipping addresses and Shopify order data",
];

const DELETED_ITEMS = [
  "Every access grant except the default one, and their order links",
  "Intake / patient form submissions - all orders",
  "Lab analysis / results data - all orders",
  "QR code labels and DHL shipping labels - all orders",
  "Report PDFs in S3 - every lab test, every order",
  "If they hold the therapist role: the role, its application history, and uploaded documents",
  "Their login state - password, verification, consent flags, and any pending signup/reset links",
];

// Two-stage: "confirm" (reason + type-to-confirm, since this is irreversible)
// then "result" (full breakdown of what the backend actually deleted). The
// dialog stays open on the result step so the admin can't miss it; onDeleted
// only fires once they explicitly close out of that step.
function DeleteCustomerDataDialog({ open, customerId, customerLabel, onClose, onDeleted }) {
  const [reason, setReason] = React.useState("");
  const [confirmText, setConfirmText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [result, setResult] = React.useState(null);

  React.useEffect(() => {
    if (open) {
      setReason("");
      setConfirmText("");
      setError("");
      setResult(null);
    }
  }, [open]);

  const canSubmit = reason.trim().length > 0 && confirmText.trim() === "DELETE" && !loading;

  const handleDelete = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const res = await deleteCustomerData(customerId, reason.trim());
      setResult(res?.data || {});
    } catch (err) {
      setError(err.message || "Couldn't delete this customer's data.");
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    onDeleted(result);
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : result ? handleDone : onClose}
      maxWidth="xs"
      fullWidth
    >
      {result ? (
        <>
          <DialogTitle>Customer data deleted</DialogTitle>
          <DialogContent>
            <Alert severity="success" sx={{ mb: 2 }}>
              {customerLabel ? `${customerLabel}'s` : "This customer's"} portal data has been permanently deleted.
            </Alert>
            <DeletionSummary result={result} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
            <Button onClick={handleDone} variant="contained" autoFocus>
              Done
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>Delete customer data</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This is permanent and cannot be undone. There is no backup to restore from.
            </Alert>

            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
              Will be deleted
            </Typography>
            <Box component="ul" sx={{ m: 0, mb: 1.5, pl: 2.5 }}>
              {DELETED_ITEMS.map((item) => (
                <Typography key={item} component="li" sx={{ fontSize: "0.75rem", color: "#4b5563", mb: 0.25 }}>
                  {item}
                </Typography>
              ))}
            </Box>

            <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", mb: 0.5 }}>
              Will be kept
            </Typography>
            <Box component="ul" sx={{ m: 0, mb: 2, pl: 2.5 }}>
              {KEPT_ITEMS.map((item) => (
                <Typography key={item} component="li" sx={{ fontSize: "0.75rem", color: "#4b5563", mb: 0.25 }}>
                  {item}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={2}
              label="Reason"
              placeholder="e.g. Customer requested deletion via support ticket #4521"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <DialogContentText sx={{ fontSize: "0.75rem", mb: 1 }}>
              Type <strong>DELETE</strong> to confirm.
            </DialogContentText>
            <TextField
              fullWidth
              placeholder="DELETE"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              disabled={loading}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
            <Button onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleDelete} color="error" variant="contained" disabled={!canSubmit}>
              {loading ? "Deleting..." : "Delete permanently"}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default DeleteCustomerDataDialog;
